"use server";

import { cookies, headers } from "next/headers";
import { redirect } from "next/navigation";
import { cache } from "react";
import { apiFetch, ApiError, isMaintenanceApiError } from "./api";
import {
  getAccessTokenCookieOptions,
  isSecureAuthRequest,
} from "./auth-cookie";
import { ACCESS_TOKEN_COOKIE } from "./constants";
import type { User } from "./types";

type LoginResponse = {
  accessToken: string;
  user: User;
};

type ChangePasswordResponse = {
  accessToken: string;
  changed: true;
};

export type LoginState = {
  message?: string;
  retryAfterSeconds?: number;
};

async function isSecureRequest() {
  const requestHeaders = await headers();
  const protocol = requestHeaders.get("x-forwarded-proto");
  const origin = requestHeaders.get("origin");
  const referer = requestHeaders.get("referer");

  return isSecureAuthRequest({
    forwardedProto: protocol,
    origin,
    referer,
  });
}

export const getAccessToken = cache(async function getAccessToken() {
  return (await cookies()).get(ACCESS_TOKEN_COOKIE)?.value;
});

export const getCurrentUser = cache(async function getCurrentUser() {
  const token = await getAccessToken();

  if (!token) {
    return null;
  }

  try {
    return await apiFetch<User>("/auth/me", { token });
  } catch (error) {
    if (error instanceof ApiError && error.status === 401) {
      return null;
    }

    if (isMaintenanceApiError(error)) {
      redirect("/maintenance");
    }

    throw error;
  }
});

export async function loginAction(
  _previousState: LoginState,
  formData: FormData,
): Promise<LoginState> {
  const email = String(formData.get("email") ?? "").trim();
  const password = String(formData.get("password") ?? "");

  if (!email || !password) {
    return { message: "Informe e-mail e senha." };
  }

  let redirectTo = "/app";
  const secureCookie = await isSecureRequest();

  try {
    const session = await apiFetch<LoginResponse>("/auth/login", {
      method: "POST",
      body: JSON.stringify({ email, password }),
    });

    (await cookies()).set(
      ACCESS_TOKEN_COOKIE,
      session.accessToken,
      getAccessTokenCookieOptions(secureCookie),
    );

    if (session.user.mustChangePassword) {
      redirectTo = "/change-password";
    }
  } catch (error) {
    if (error instanceof ApiError) {
      if (error.status === 429) {
        return {
          message: "Muitas tentativas. Aguarde antes de tentar novamente.",
          retryAfterSeconds: error.retryAfterSeconds,
        };
      }

      return { message: error.message };
    }

    return { message: "Não foi possível conectar ao servidor." };
  }

  redirect(redirectTo);
}

export async function logoutAction() {
  const token = await getAccessToken();

  if (token) {
    try {
      await apiFetch("/auth/logout", { method: "POST", token });
    } catch {
      // The local cookie must still be removed when the API session expired.
    }
  }

  (await cookies()).delete(ACCESS_TOKEN_COOKIE);
  redirect("/login");
}

export async function expireSessionAction() {
  (await cookies()).delete(ACCESS_TOKEN_COOKIE);
  redirect("/login");
}

export async function changePasswordAction(
  _previousState: LoginState,
  formData: FormData,
): Promise<LoginState> {
  const token = await getAccessToken();
  const password = String(formData.get("password") ?? "");
  const confirmPassword = String(formData.get("confirmPassword") ?? "");

  if (!token) {
    redirect("/login");
  }

  if (password.length < 6) {
    return { message: "A senha precisa ter pelo menos 6 caracteres." };
  }

  if (password !== confirmPassword) {
    return { message: "As senhas não conferem." };
  }

  try {
    const session = await apiFetch<ChangePasswordResponse>(
      "/auth/change-password",
      {
        method: "POST",
        token,
        body: JSON.stringify({ password }),
      },
    );
    const secureCookie = await isSecureRequest();
    (await cookies()).set(
      ACCESS_TOKEN_COOKIE,
      session.accessToken,
      getAccessTokenCookieOptions(secureCookie),
    );
  } catch (error) {
    if (error instanceof ApiError) {
      return { message: error.message };
    }

    return { message: "Não foi possível alterar a senha." };
  }

  redirect("/app");
}
